import React, { useCallback, useEffect, useState } from "react";
import { Dialog, Slide } from "@mui/material";
import SocieteAndUserService from "../services/societeAndUser";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const GREEN = "#28a745";

const CATALOG_LABELS = [
  { key: "family", label: "Famille « DEFAUT »" },
  { key: "subFamily", label: "Sous-famille « DEFAUT »" },
  { key: "category", label: "Catégorie « DEFAUT »" },
  { key: "brand", label: "Marque « DEFAUT »" },
];

const Status = ({ ok, label, detail }) => (
  <li className="py-1 d-flex align-items-center">
    <i
      className={`fas ${ok ? "fa-check-circle" : "fa-times-circle"} mr-2`}
      style={{ color: ok ? GREEN : "#dc3545" }}
      aria-hidden="true"
    />
    <span>{label}</span>
    <span className={`ml-2 small ${ok ? "text-muted" : "text-danger"}`}>{detail || (ok ? "existe déjà" : "à créer")}</span>
  </li>
);

const rolesLabel = (roles) => (roles && roles.length > 0 ? roles.join(", ") : "sans rôle");

// What a company that already exists is missing of its defaults (central warehouse and DEFAUT family / sub-family /
// category / brand): only what is missing, and ticked, is created.
const SocieteDefaultsDialog = ({ societe, onClose, onDone }) => {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [status, setStatus] = useState(null);
  const [wantWarehouse, setWantWarehouse] = useState(true);
  const [wantCatalog, setWantCatalog] = useState(true);
  const [manager, setManager] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null); // { lines: [], problems: [] }

  const load = useCallback(() => {
    setState("loading");
    SocieteAndUserService.getSocieteDefaultsStatus(societe.id)
      .then((response) => {
        const data = response.data;
        if (!data || data.error || !data.warehouse || !data.catalog) {
          setState("error");
          return;
        }
        setStatus(data);
        // a warehouse manager of the company is the natural manager of its central warehouse
        const existingManager = (data.users || []).find((user) => (user.roles || []).includes("WAREHOUSE_MANAGER"));
        setManager(existingManager ? String(existingManager.id) : "");
        setState("ready");
      })
      .catch(() => setState("error"));
  }, [societe.id]);

  useEffect(() => {
    load();
  }, [load]);

  const missingWarehouse = status ? !status.warehouse.exists : false;
  const missingCatalog = status ? CATALOG_LABELS.some((item) => !status.catalog[item.key]) : false;
  const toCreate = (missingWarehouse && wantWarehouse) || (missingCatalog && wantCatalog);

  const onCreate = () => {
    if (busy || !toCreate) return;
    setBusy(true);
    setReport(null);
    SocieteAndUserService.initSocieteDefaults({
      idSociete: societe.id,
      centralWarehouse: missingWarehouse && wantWarehouse,
      defaultCatalog: missingCatalog && wantCatalog,
      idWarehouseManager: missingWarehouse && wantWarehouse && manager ? Number(manager) : null,
    })
      .then((response) => {
        const data = response.data || {};
        const lines = [];
        if (data.warehouse && data.warehouse.existing === false) lines.push("Dépôt central créé");
        const created = data.catalog && data.catalog.created;
        if (created) {
          if (created.family) lines.push("Famille « DEFAUT » créée");
          if (created.subFamily) lines.push("Sous-famille « DEFAUT » créée");
          if (created.category) lines.push("Catégorie « DEFAUT » créée");
          if (created.brand) lines.push("Marque « DEFAUT » créée");
        }
        const problems = (data.error ? [data.error] : []).concat(data.errors || []);
        setReport({ lines, problems });
        if (lines.length > 0 && onDone) onDone(lines);
        load();
      })
      .catch(() => setReport({ lines: [], problems: ["Erreur : problème de connexion"] }))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={true} TransitionComponent={Transition} keepMounted onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <div className="modal-header" style={{ borderBottom: `3px solid ${GREEN}` }}>
        <div>
          <h5 className="modal-title font-weight-bold">
            <i className="fas fa-cogs mr-2" style={{ color: GREEN }} />
            Configuration par défaut
          </h5>
          <div className="text-muted small">{societe.title}</div>
        </div>
      </div>

      <div className="modal-body" style={{ overflowY: "auto" }}>
        {state === "loading" && (
          <div className="text-center py-4">
            <div className="spinner-border text-success" role="status">
              <span className="sr-only">Chargement...</span>
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center mb-0">
            <span>Impossible de lire la configuration de la société.</span>
            <button className="btn btn-sm btn-outline-danger" onClick={load}>
              <i className="fas fa-redo mr-1"></i>Réessayer
            </button>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="text-uppercase font-weight-bold small mb-2" style={{ color: GREEN }}>
              <i className="fas fa-warehouse mr-2" />
              Dépôt central
            </div>
            <ul className="list-unstyled mb-2">
              <Status ok={status.warehouse.exists} label="Dépôt central" />
            </ul>
            {missingWarehouse && (
              <div className="mb-4 ml-4">
                <div className="custom-control custom-checkbox mb-2">
                  <input
                    id="defaults-want-warehouse"
                    type="checkbox"
                    className="custom-control-input"
                    checked={wantWarehouse}
                    onChange={(e) => setWantWarehouse(e.target.checked)}
                  />
                  <label className="custom-control-label" htmlFor="defaults-want-warehouse">
                    Créer le dépôt central
                  </label>
                </div>
                {wantWarehouse && (
                  <div className="form-group mb-0">
                    <label htmlFor="defaults-manager" className="small text-muted mb-1">
                      Responsable du dépôt
                    </label>
                    <select
                      id="defaults-manager"
                      className="form-control form-control-sm"
                      value={manager}
                      onChange={(e) => setManager(e.target.value)}
                    >
                      <option value="">Administrateur de la société (par défaut)</option>
                      {(status.users || []).map((user) => (
                        <option key={user.id} value={String(user.id)}>
                          {user.name || user.email} ({rolesLabel(user.roles)})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="text-uppercase font-weight-bold small mb-2 mt-3" style={{ color: GREEN }}>
              <i className="fas fa-tags mr-2" />
              Catalogue par défaut
            </div>
            <ul className="list-unstyled mb-2">
              {CATALOG_LABELS.map((item) => (
                <Status key={item.key} ok={status.catalog[item.key]} label={item.label} />
              ))}
            </ul>
            {missingCatalog && (
              <div className="custom-control custom-checkbox mb-2 ml-4">
                <input
                  id="defaults-want-catalog"
                  type="checkbox"
                  className="custom-control-input"
                  checked={wantCatalog}
                  onChange={(e) => setWantCatalog(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="defaults-want-catalog">
                  Créer les éléments manquants
                </label>
              </div>
            )}

            {!missingWarehouse && !missingCatalog && (
              <div className="alert alert-success mt-3 mb-0">
                <i className="fas fa-check-circle mr-2" />
                Tout est déjà en place : il n'y a rien à créer.
              </div>
            )}
          </>
        )}

        {report && (
          <div className="mt-3" aria-live="polite">
            {report.lines.length > 0 && (
              <div className="alert alert-success mb-2">
                <ul className="mb-0 pl-3">
                  {report.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            {report.problems.length > 0 && (
              <div className="alert alert-danger mb-0">
                <ul className="mb-0 pl-3">
                  {report.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
          Fermer
        </button>
        {state === "ready" && (missingWarehouse || missingCatalog) && (
          <button type="button" className="btn btn-success" onClick={onCreate} disabled={busy || !toCreate}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Création en cours...
              </>
            ) : (
              <>
                <i className="fas fa-plus-circle mr-2" />
                Créer
              </>
            )}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default SocieteDefaultsDialog;
